import { PATHS } from 'routes/routes.constants';

import { LocalStorageWithFallback } from 'lib/opentable/callback/localStorageWithFallback';
import {
  OT_AUTH_PARAMS,
  OT_CALLBACK_QUERY_KEY,
  OT_CALLBACK_QUERY_VALUE,
  OT_USER_LOCALSTORAGE_KEY,
  OT_USER_LOCALSTORAGE_VALUE,
} from './constants';

/**
 * Generates the callback search parameters string for OpenTable integration.
 * @return {string} The generated callback search parameters string.
 */
export const generateOTCallbackSearchParams = (): string => {
  return new URLSearchParams({
    [OT_CALLBACK_QUERY_KEY]: OT_CALLBACK_QUERY_VALUE,
  }).toString();
};

/**
 * Checks if the current user is an OpenTable user based on URL parameters.
 * @return {boolean} Whether the current user is an OpenTable user.
 */
export const isOpenTableLogin = (): boolean =>
  new URLSearchParams(window.location.search).get(OT_CALLBACK_QUERY_KEY) ===
  OT_CALLBACK_QUERY_VALUE;

/**
 * Handles setting up localStorage flag for OpenTable auth redirects to ensure
 * @param {boolean} isOTUser - Optional flag to explicitly set OT user status.
 * the correct Auth0 login screen is shown.
 */
export const handleOTAuthRedirectSetup = (isOTUser: boolean) => {
  const localStorage = new LocalStorageWithFallback();

  // Set localStorage flag if this is an OpenTable callback
  if (isOTUser) {
    localStorage.set(OT_USER_LOCALSTORAGE_KEY, OT_USER_LOCALSTORAGE_VALUE);
  } else {
    localStorage.remove(OT_USER_LOCALSTORAGE_KEY);
  }
};

/** Checks if the current user is an OpenTable user based on localStorage flag.
 * @return {boolean} Whether the current user is an OpenTable user.
 */
export const isOTUserSet = (): boolean => {
  const localStorage = new LocalStorageWithFallback();

  return (
    localStorage.get<string>(OT_USER_LOCALSTORAGE_KEY) ===
    OT_USER_LOCALSTORAGE_VALUE
  );
};

/** Checks if the current user is an OpenTable user based on URL parameters or
 * localStorage flag.
 * @return {boolean} Whether the current user is an OpenTable user.
 */
export const isOpenTableUser = () => isOTUserSet() || isOpenTableLogin();

/**
 * Gets the Auth0 parameters for OpenTable users based on where the user has
 * logged in from.
 * @return {Record<string, string>} The Auth0 parameters for OpenTable users.
 */
export const getOTAuth0Params = (): Record<string, string> => {
  let isOTUser: boolean;
  const pathname = window.location.pathname;

  // Setting OT user cookies is restricted to callback paths only
  if (pathname === PATHS.CALLBACK || pathname === PATHS.OPENTABLE_CALLBACK) {
    // Only set OT user flag if on callback path and they are an OT user
    isOTUser = pathname === PATHS.CALLBACK ? isOpenTableLogin() : true;

    handleOTAuthRedirectSetup(isOTUser);
  } else {
    isOTUser = isOTUserSet();
  }

  return isOTUser ? OT_AUTH_PARAMS : {};
};
